import { useCallback, useEffect, useState, type SetStateAction } from 'react'

function readStoredValue<T>(key: string, fallback: () => T): T {
  if (typeof window === 'undefined') {
    return fallback()
  }

  try {
    const raw = window.localStorage.getItem(key)
    if (raw === null) {
      return fallback()
    }
    return JSON.parse(raw) as T
  } catch {
    return fallback()
  }
}

export function useLocalState<T>(
  key: string,
  getDefaultValue: () => T,
): [T, (value: SetStateAction<T>) => void] {
  const [state, setState] = useState<T>(() =>
    readStoredValue(key, getDefaultValue),
  )

  const setPersistedState = useCallback((value: SetStateAction<T>) => {
    setState((prev) => {
      const nextValue =
        typeof value === 'function'
          ? (value as (previous: T) => T)(prev)
          : value
      return nextValue
    })
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    try {
      window.localStorage.setItem(key, JSON.stringify(state))
    } catch {
      // Ignore storage write errors
    }
  }, [key, state])

  return [state, setPersistedState]
}
